import { getAuthHeaders, isAuthenticated } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.centroautomotrizhlfrd.com.do/api';

const redirectToLogin = () => {
  localStorage.removeItem('client-auth-storage');
  window.location.href = '/login';
};

const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
  if (!isAuthenticated()) {
    redirectToLogin();
    throw new Error('No hay sesión activa');
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  };

  const response = await fetch(url, config);

  if (response.status === 401) {
    redirectToLogin();
    throw new Error('Sesión expirada');
  }

  return response;
};

export interface ServiceCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
}

export interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
  is_active: boolean;
  category: ServiceCategory;
  created_at: string;
  updated_at: string;
}

export interface ServiceClient extends Service {
  shortDescription: string;
  imageUrl?: string;
  gallery: string[];
  features: string[];
  requirements: string[];
  isPopular: boolean;
  rating: number;
  reviewCount: number;
  estimatedTime: string;
}

// Función para obtener todos los servicios
export const getServices = async (categoryId?: string): Promise<Service[]> => {
  try {
    let url = `${API_BASE_URL}/services`;
    if (categoryId) {
      url += `?category_id=${categoryId}`;
    }

    const response = await apiRequest(url);

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const services = await response.json();
    return services;
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error; // No usar fallback, mostrar el error real
  }
};


// Función para obtener un servicio específico
export const getService = async (id: string): Promise<Service> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/services/${id}`);

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const service = await response.json();
    return service;
  } catch (error) {
    console.error('Error fetching service:', error);
    throw error;
  }
};

// Función para obtener categorías de servicios
export const getServiceCategories = async (): Promise<ServiceCategory[]> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/service-categories`);

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const categories = await response.json();
    return categories;
  } catch (error) {
    console.error('Error fetching service categories:', error);
    throw error; // No usar fallback, mostrar el error real
  }
};


// Función para transformar Service a ServiceClient
export const transformServiceToClient = (service: Service): ServiceClient => {
  return {
    ...service,
    shortDescription: service.description.length > 50
      ? service.description.substring(0, 50) + '...'
      : service.description,
    imageUrl: `https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&sig=${service.id}`,
    gallery: [],
    features: [
      'Servicio profesional',
      'Garantía incluida',
      'Técnicos certificados'
    ],
    requirements: [],
    isPopular: false,
    rating: 4.5,
    reviewCount: 0,
    estimatedTime: `${service.duration} min`
  };
};
